import { ScrollStrategyOptions } from '@angular/cdk/overlay';
import {
  Component,
  ElementRef,
  Inject,
  NgZone,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { RejectReason } from 'src/app/shared/models/RejectReason';
import { HormigonDispatchFacade } from '../../hormigon-dispatch.facade';
import { SuccessDialogComponent } from 'src/app/shared/ui/success-dialog/success-dialog.component';
import { take } from 'rxjs/operators';

export interface RechazoCamionDialogData {
  order: any;
}

@Component({
    selector: 'app-hormigon-rechazo-camion',
    templateUrl: './rechazo-camion.component.html',
    styleUrls: ['./rechazo-camion.component.css'],
    standalone: false
})
export class HormigonRechazoCamionComponent implements OnInit {
  rejectForm = new UntypedFormGroup({
    rejectReasons: new UntypedFormControl(''),
    rejectObservations: new UntypedFormControl(''),
  });

  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
  images: { file: File; preview: string; sizeText: string }[] = [];

  // 🔥 modal interno confirmación/éxito
  @ViewChild('modalContent') modalContent!: TemplateRef<any>;
  confirmDialogRef?: MatDialogRef<any>;

  rejectReasons: RejectReason[] = [];
  // rejectReasonsSelected: RejectReason[] = [];
  selectedRejectReason: RejectReason | null = null;

  isConfirm = false;
  isSelectOpen = false;

  order: any;

  constructor(
    private readonly dispatchFacade: HormigonDispatchFacade,

    // modal EXTERNO (este componente está dentro de un MatDialog)
    private outerDialogRef: MatDialogRef<HormigonRechazoCamionComponent>,
    @Inject(MAT_DIALOG_DATA) public data: RechazoCamionDialogData,

    // modal INTERNO (confirmación/éxito)
    private dialog: MatDialog,
    private scrollStrategyOptions: ScrollStrategyOptions,

    private router: Router,
    private ngZone: NgZone,
  ) {
    this.order = data?.order;
  }

  ngOnInit(): void {
    this.dispatchFacade.getRejectReasons().subscribe((data) => {
      this.rejectReasons = this.transformData(data);
    });
  }

  private transformData(data: any): RejectReason[] {
    if (Array.isArray(data)) {
      return data.map((reason: any) => new RejectReason(
        Number(reason?.id ?? 0),
        String(reason?.description ?? reason ?? ''),
        String(reason?.neg ?? '')
      ));
    }

    return Object.keys(data).reduce((acc: RejectReason[], neg) => {
      const entries = Object.entries(data[neg]).map(([id, description]) =>
        new RejectReason(Number(id), description.toString(), neg)
      );
      return acc.concat(entries);
    }, []);
  }

  // ✅ Cerrar modal externo (volver)
  close() {
    this.outerDialogRef.close({ action: 'close' });
  }

  openFileSelector() {
    this.fileInput.nativeElement.click();
  }

  onFileSelected(event: any) {
    const files: FileList = event.target.files;
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (!this.isValidFile(file)) continue;

      const sizeText = file.size < 1024 * 1024
        ? `${Math.round(file.size / 1024)} KB`
        : `${(file.size / (1024 * 1024)).toFixed(1)} MB`;

      const reader = new FileReader();
      reader.onload = (e: any) => {
        this.ngZone.run(() => {
          this.images.push({ file, preview: e.target.result, sizeText });
        });
      };
      reader.readAsDataURL(file);
    }
    this.fileInput.nativeElement.value = '';
  }

  isValidFile(file: File): boolean {
    const allowedExtensions = ['.jpg', '.jpeg', '.png', '.gif'];
    const parts = file.name.split('.');
    const ext = `.${parts[parts.length - 1]}`.toLowerCase();
    return allowedExtensions.includes(ext);
  }

  // ✅ abre modal interno en pantalla de confirmación
  confirmModal() {
    // this.isConfirm = false;

    // this.confirmDialogRef = this.dialog.open(this.modalContent, {
    //   maxWidth: '90%',
    //   scrollStrategy: this.scrollStrategyOptions.noop(),
    //   disableClose: true,
    // });

    this.submit()
  }

  // ✅ cerrar modal interno (seguir editando / X)
  closeConfirmModal() {
    this.confirmDialogRef?.close();
  }

  // Confirma el rechazo directo o lo deja guardado para reintento.
  async submit() {
    const tkt = this.order?.tkt?.toString() ?? '';
    const orderNumber = this.order?.orderNumber?.toString() ?? '';
    if (!tkt.trim() || !orderNumber.trim()) {
      console.error(
        '[RechazoCamion] No se puede enviar rechazo: tkt u orderNumber faltante',
        { tkt, orderNumber }
      );
      return;
    }

    const reasons = this.selectedRejectReason?.description ?? '';
    const files = this.images.map(img => ({ name: img.file.name, base64: img.preview }));

    await this.dispatchFacade.reject(
      tkt,
      orderNumber,
      reasons,
      this.rejectForm.value.rejectObservations,
      this.images.map(img => img.file),
      '',
      files
    ).pipe(take(1)).toPromise();

    this.outerDialogRef.close({ action: 'success' });
  }


  // ✅ aceptar en éxito: cierra modal interno y cierra modal externo devolviendo confirm
  ok() {
    this.closeConfirmModal();
    this.outerDialogRef.close({ action: 'confirm' });

    // Si NO quieres navegar aquí (porque ya estás en modal dentro del flujo actual), quítalo.
    this.router.navigate(['/pedidospendiente']);
  }

  openSelect() {
    this.isSelectOpen = !this.isSelectOpen;
  }

  // clickReason(reason: RejectReason) {
  //   if (this.rejectReasonsSelected.some((x) => x.id === reason.id))
  //     this.removeReason(reason);
  //   else this.selectReason(reason);
  // }

  // selectReason(reason: RejectReason) {
  //   this.rejectReasonsSelected.push(reason);
  // }

  // removeReason(reason: RejectReason) {
  //   this.rejectReasonsSelected = this.rejectReasonsSelected.filter(
  //     (item) => item.id !== reason.id
  //   );
  // }

  // isSelected(reason: RejectReason) {
  //   return this.rejectReasonsSelected.some((x) => x.id === reason.id);
  // }

  // isDisabled() {
  //   return this.rejectReasonsSelected.length === 0;
  // }
selectReason(reason: RejectReason) {
  this.selectedRejectReason = reason;
  this.isSelectOpen = false; // cierra dropdown al elegir
}

removeReason() {
  this.selectedRejectReason = null;
}

isSelected(reason: RejectReason) {
  return this.selectedRejectReason?.id === reason.id;
}

isDisabled(): boolean {
  const hasReason = !!this.selectedRejectReason;
  const obs = (this.rejectForm.value.rejectObservations || '').toString().trim();
  const hasObs = obs.length > 0;

  return !(hasReason && hasObs);
}


  removeImage(image: any) {
    const index = this.images.indexOf(image);
    if (index !== -1) this.images.splice(index, 1);
  }

  openRejectTruck(order: any) {
  const ref = this.dialog.open(HormigonRechazoCamionComponent, {
    width: '760px',
    maxWidth: '95vw',
    data: { order },
    disableClose: true,
  });

  ref.afterClosed().subscribe((res) => {
    if (res?.action === 'success') {
      this.dialog.open(SuccessDialogComponent, {
        width: '520px',
        maxWidth: '95vw',
        disableClose: true,
        data: {
          title: 'Rechazo enviado con éxito',
          message: 'Esta acción no se puede deshacer',
        },
      });

      // ✅ si necesitas refrescar data:
      // this.getOrders();
    }
  });
}
}
